import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from './utils/Toast';
import Loader from './utils/Loader';
import Navbar from './utils/Navbr'; 
import './style/PerformanceReport.css';

// --- CUSTOM DATE PICKER COMPONENT ---
const CustomDatePicker = ({ label, selectedDate, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState(new Date(selectedDate || new Date()));
    const containerRef = useRef(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Calendar Logic
    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const handleDayClick = (day) => {
        const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
        // Format YYYY-MM-DD for consistency
        const formatted = newDate.toISOString().split('T')[0];
        onChange(formatted);
        setIsOpen(false);
    };

    const changeMonth = (offset) => {
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + offset, 1));
    };

    const renderCalendar = () => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = getFirstDayOfMonth(year, month);
        
        const blanks = Array(firstDay).fill(null);
        const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
        
        const allSlots = [...blanks, ...days];
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        return (
            <div className="custom-calendar-unique">
                <div className="cal-header">
                    <button onClick={(e) => { e.stopPropagation(); changeMonth(-1); }}>&lt;</button>
                    <span>{monthNames[month]} {year}</span>
                    <button onClick={(e) => { e.stopPropagation(); changeMonth(1); }}>&gt;</button>
                </div>
                <div className="cal-grid">
                    {['S','M','T','W','T','F','S'].map(d => <div key={d} className="cal-day-name">{d}</div>)}
                    {allSlots.map((day, i) => {
                        if (!day) return <div key={i} className="cal-empty"></div>;
                        
                        // Check if selected
                        const currentStr = new Date(year, month, day).toDateString();
                        const selectedStr = new Date(selectedDate).toDateString();
                        const isSelected = currentStr === selectedStr;

                        return (
                            <div 
                                key={i} 
                                className={`cal-day ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleDayClick(day)}
                            >
                                {day}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="custom-date-wrapper-unique" ref={containerRef}>
            <label className="custom-label">{label}</label>
            <div 
                className={`custom-input-box ${isOpen ? 'active' : ''}`} 
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="icon-glass">📅</span>
                <span className="date-text">{selectedDate || 'Select Date'}</span>
            </div>
            {isOpen && renderCalendar()}
        </div>
    );
};

// --- MAIN PAGE COMPONENT ---
const PerformanceReport = () => {
    const navigate = useNavigate();

    // --- State ---
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [reportData, setReportData] = useState([]);
    
    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [dateRange, setDateRange] = useState({
        startDate: new Date().toISOString().slice(0, 10).substring(0, 8) + '01', 
        endDate: new Date().toISOString().slice(0, 10) 
    });

    // --- Helper: Get Cookie ---
    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    };

    // --- Fetch Data ---
    const fetchData = async () => {
        const authToken = getCookie('authToken');
        
        try {
            setLoading(true);

            // 1. Auth Check
            const authRes = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'checkAuth',
                authToken
            });

            if (authRes.data.status === false) {
                toast.error(authRes.data.message || 'Session expired');
                navigate('/login');
                return;
            }
            setUser(authRes.data.res);

            // 2. Fetch Report Data
            const reportRes = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'getPerformanceReport', 
                authToken,
                startDate: dateRange.startDate,
                endDate: dateRange.endDate
            });

            if (reportRes.data.status) {
                setReportData(reportRes.data.report || []);
            }

        } catch (error) {
            console.error(error);
            toast.error('Failed to load report data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange]); 

    // --- Filter Logic ---
    const filteredData = useMemo(() => {
        if (!searchQuery) return reportData;
        const lowerQ = searchQuery.toLowerCase();
        return reportData.filter(item => 
            item.name.toLowerCase().includes(lowerQ) || 
            String(item.id).includes(lowerQ)
        );
    }, [reportData, searchQuery]);

    const handleRowClick = (offerId) => {
        navigate(`/reports/clicks?offerId=${offerId}&start=${dateRange.startDate}&end=${dateRange.endDate}`);
    };

    if (loading) return <Loader />;

    return (
        <div className="report-page-wrapper-unique">
            <div className="bg-shape-rep shape-r1"></div>
            <div className="bg-shape-rep shape-r2"></div>

            <Navbar name={user?.name} role={user?.role} />

            <div className="report-container-unique">
                
                <div className="report-header-unique">
                    <h2>Performance Report</h2>
                    <p>Analyze traffic, conversions, and earnings.</p>
                </div>

                {/* --- Custom Filters Section --- */}
                <div className="filters-card-unique">
                    
                    {/* Top Row: Search */}
                    <div className="search-section-unique">
                        <div className="input-group-glass-unique full-width">
                            <span className="icon-glass">🔍</span>
                            <input 
                                type="text" 
                                placeholder="Search Offer Name or ID..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Bottom Row: Date Pickers & Actions */}
                    <div className="controls-row-unique">
                        
                        <CustomDatePicker 
                            label="Start Date" 
                            selectedDate={dateRange.startDate} 
                            onChange={(date) => setDateRange(prev => ({ ...prev, startDate: date }))} 
                        />

                        <div className="date-arrow-unique">➜</div>

                        <CustomDatePicker 
                            label="End Date" 
                            selectedDate={dateRange.endDate} 
                            onChange={(date) => setDateRange(prev => ({ ...prev, endDate: date }))} 
                        />

                        {/* Spacer */}
                        <div className="controls-spacer"></div>

                        {/* Refresh Button */}
                        <button className="refresh-btn-unique " onClick={fetchData} title="Refresh Data">
                            <span>REFRESH</span> 🔄
                        </button>
                    </div>

                </div>

                {/* --- Data Table --- */}
                <div className="table-card-unique">
                    <div className="table-responsive-unique">
                        <table className="report-table-unique">
                            <thead>
                                <tr>
                                    <th>Offer</th>
                                    <th>ID</th>
                                    <th className="text-right">Unique Clicks</th>
                                    <th className="text-right">Total Clicks</th>
                                    <th className="text-right">Conv.</th>
                                    <th className="text-right">Rejected</th>
                                    <th className="text-right">Earnings</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.length > 0 ? (
                                    filteredData.map((row) => (
                                        <tr 
                                            key={row.id} 
                                            onClick={() => handleRowClick(row.id)}
                                            className="clickable-row-unique"
                                        >
                                            <td className="offer-cell-unique">
                                                <img src={row.logo} alt="" className="offer-logo-mini" />
                                                <span className="offer-name-text">{row.name}</span>
                                            </td>
                                            <td className="mono-text">#{row.id}</td>
                                            <td className="text-right">{row.uniqueClicks.toLocaleString()}</td>
                                            <td className="text-right">{row.clicks.toLocaleString()}</td>
                                            <td className="text-right highlight-conv">{row.conversions}</td>
                                            <td className="text-right rejected-text">{row.rejected}</td>
                                            <td className="text-right highlight-earn">${row.earnings.toFixed(2)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="no-data-cell">No performance data found.</td>
                                    </tr>
                                )}
                            </tbody>
                            {filteredData.length > 0 && (
                                <tfoot>
                                    <tr>
                                        <td colSpan="2"><strong>Total</strong></td>
                                        <td className="text-right"><strong>{filteredData.reduce((a,b)=>a+b.uniqueClicks,0).toLocaleString()}</strong></td>
                                        <td className="text-right"><strong>{filteredData.reduce((a,b)=>a+b.clicks,0).toLocaleString()}</strong></td>
                                        <td className="text-right"><strong>{filteredData.reduce((a,b)=>a+b.conversions,0)}</strong></td>
                                        <td className="text-right"><strong>{filteredData.reduce((a,b)=>a+b.rejected,0)}</strong></td>
                                        <td className="text-right earn-total"><strong>${filteredData.reduce((a,b)=>a+b.earnings,0).toFixed(2)}</strong></td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default PerformanceReport;